<template>
  <div>
    <div class="f-flex f-justify-between">
      <BaseRadio
        ref="radio"
        name="shipping_method"
        :value="shippingMethod.shippingMethodResource.id"
        :id="$id('shipping-' + shippingMethod.shippingMethodResource.id)"
        @input="shippingMethodChanged"
        v-model="selected"
        v-on:click="showGlsWidget"
      >
        <span
          :aria-label="`${
            shippingMethod.shippingMethodResource.name
          }, cena: ${formattedPrice(price)}`"
        >
          {{ shippingMethod.shippingMethodResource.name }}
        </span>
        <ShippingPopover
          v-if="shippingDescription"
          :description="shippingDescription"
        ></ShippingPopover>
        <div v-show="freeOfCharge" class="f-text-sm">
          {{ formattedFreeOfCharge(freeOfCharge) }}
        </div>
      </BaseRadio>
      <div v-if="this.freeShipping">
        <div class="f-text-right f-text-xs f-line-through">
          {{ formattedPrice(price) }}
        </div>
        <div
          class="f-text-right"
          v-bind:class="{ 'f-text-red-600': this.freeShipping }"
        >
          {{ $t("prices.free").toUpperCase() }}
        </div>
      </div>
      <div v-if="!this.freeShipping">
        {{ formattedPrice(price) }}
      </div>
    </div>
    <ValidationProvider
      :rules="rulesObject"
      :skipIfEmpty="false"
      v-slot="{ errors }"
    >
      <div v-show="isSelected">
        <label>
          <select
            class="f-form-select f-border-gray-300 f-rounded f-w-full f-mt-2 f-py-1 f-text-gray-700"
            ref="select"
            :id="
              $id('shipping-select-' + shippingMethod.shippingMethodResource.id)
            "
            :name="shippingMethod.shippingMethodResource.id + '_gls_branch_id'"
            v-model="selectedOption"
            v-on:click="showGlsWidget"
          >
            <option
              v-for="option in options"
              :value="option.value"
              :key="option.value"
            >
              {{ option.text }}
            </option>
          </select>
        </label>
        <div v-if="getFirstError(errors)" class="f-text-sm f-text-red-600">
          {{ getFirstError(errors) }}
        </div>
      </div>
    </ValidationProvider>
  </div>
</template>

<script lang="ts">
import FormShippingItem from "@/module/FormShippingItem";
import FormResource from "@/module/Form/Resources/FormResource";
import InputValidationMixin from "@/components/Form/Basic/Inputs/InputValidationMixin";
import mixins from "vue-typed-mixins";
import { ValidationProvider } from "vee-validate";
import Vue, { PropType } from "vue";
import {
  isObjectOrNull,
  isStringOrNull,
} from "@/module/ComponentPropsValidations/validations";
import BaseRadio from "@/components/Form/Basic/Inputs/BaseRadio.vue";
import money from "@/module/Filters/MoneyFilter";
import ShippingPopover from "@/components/Form/ShippingMethods/ShippingPopover.vue";
import OrderPrices from "@/module/Order/OrderPrices";
import NumberHelper from "@/module/NumberHelper";
import SelectedShippingMethod from "@/module/Shipping/SelectedShippingMethod";

interface ComponentData {
  selected: number | null;
  selectedOption: number | null;
  options: { value: string | null; text: string }[];
  freeShipping: boolean;
}

export default mixins(InputValidationMixin).extend({
  name: "GlsPersonalCollection",

  components: {
    ShippingPopover,
    ValidationProvider,
    BaseRadio,
  },

  props: {
    shippingMethod: {
      type: Object as PropType<FormShippingItem>,
      required: true,
    },
    currentlySelectedRadio: {
      type: null as unknown as PropType<SelectedShippingMethod | null>,
      required: true,
      validator: isObjectOrNull,
    },
    currency: {
      type: String,
      required: true,
    },
    order: {
      type: Object as PropType<OrderPrices>,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    totalPriceIncVat: {
      required: true,
    },
  },

  data(): ComponentData {
    return {
      selected: null,
      selectedOption: null,
      options: [],
      freeShipping: false,
    };
  },

  computed: {
    freeOfCharge(): number | null {
      if (this.shippingMethod.freeOfCharges.hasOwnProperty("noFreeShipping")) {
        return null;
      }

      const shipping_form_item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      if (shipping_form_item === undefined) {
        return null;
      }

      const price = this.shippingMethod.freeOfCharges[this.currency];
      const includingVat =
        this.shippingMethod.shippingMethodResource.including_vat;
      const currentVat = shipping_form_item.currentVat;

      if (this.formResource.show_prices_including_vat) {
        return NumberHelper.priceIncludingVat(price, currentVat, includingVat);
      }
      return NumberHelper.priceWithOutVat(price, currentVat, includingVat);
    },

    isSelected: function (): boolean {
      return (
        this.shippingMethod.shippingMethodResource.id ===
        this.currentlySelectedRadio?.shipping_method_id
      );
    },

    rulesObject(): object {
      return this.isSelected ? { required: true } : {};
    },

    price(): number {
      const item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      if (item === undefined) {
        return 0;
      }

      if (this.freeOfCharge !== null) {
        this.updateFreeShipping(this.isEligibleForFreeShipping());
      }

      if (this.formResource.show_prices_including_vat) {
        return item.currentPriceIncVat;
      }

      return item.currentPrice;
    },

    shippingDescription(): string | null {
      return this.shippingMethod.shippingMethodResource.description;
    },
  },

  methods: {
    formattedFreeOfCharge(price: number): string {
      if (price) {
        return (
          String(this.$t("shipping_method.free_shipping_from")) +
          money(price, this.$root.$i18n)
        );
      }
      return "";
    },

    updateFreeShipping(value: boolean): void {
      this.freeShipping = value;
    },

    isEligibleForFreeShippingHelper(vat: boolean): number {
      if (vat) {
        return this.order.totalPriceWoShippingIncludingVat;
      }
      return this.order.totalPriceWoShipping;
    },

    isEligibleForFreeShipping(): boolean {
      if (this.freeOfCharge !== null) {
        if (
          this.isEligibleForFreeShippingHelper(
            this.formResource.show_prices_including_vat,
          ) >= this.freeOfCharge
        ) {
          return true;
        }
      }
      return false;
    },

    formattedPrice(price: number): string {
      return money(price, this.$root.$i18n);
    },

    showGlsWidget(): void {
      const countryCodes =
        this.shippingMethod.shippingMethodResource.address_prices.map(
          (item) => item.address.country_code,
        );

      const countryCode = countryCodes.pop();

      const div = document.createElement("div");
      div.style.zIndex = "999999";
      div.style.position = "fixed";
      div.style.left = "0";
      div.style.top = "0";
      div.style.width = "100%";
      div.style.height = "100%";
      div.style.background = "rgba(0, 0, 0, 0.3)";

      const iframe = document.createElement("iframe");
      iframe.src =
        "https://ps-maps.gls-czech.com?find=1&lng=" +
        this.$root.$i18n.locale +
        "&ctrcode=" +
        countryCode;
      iframe.style.border = "hidden";
      iframe.style.position = "absolute";
      iframe.style.top = "0";
      iframe.style.left = "0";
      iframe.width = "100%";
      iframe.height = "100%";
      iframe.style.zIndex = "9999999";
      iframe.style.padding = "10px 5px";
      iframe.style.boxSizing = "border-box";
      iframe.allow = "geolocation";
      iframe.allowFullscreen = true;

      div.appendChild(iframe);
      document.body.appendChild(div);

      window.addEventListener("message", (event) => {
        const data = event.data?.parcelshop?.detail;

        if (!data) {
          return;
        }

        this.options = [
          {
            value: data.pclshopid,
            text:
              data?.name +
              ", " +
              data?.address +
              ", " +
              data?.city +
              ", " +
              data?.zipcode +
              ", " +
              data?.ctrcode,
          },
        ];
        this.selectedOption = data.pclshopid;
        this.shippingMethodChanged();

        div.remove();
      });
    },

    shippingMethodChanged(): void {
      const branch = this.options.find((item) => {
        return item.value === this.selectedOption && item.value !== null;
      });

      this.$emit("shippingMethodChanged", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        shipping_metadata: {
          gls_parcel_shop_name: branch ? branch.text : null,
          gls_parcel_shop_id: this.selectedOption,
        },
        shipping_form_item: this.shippingMethod.formItems.find((item) => {
          return item.name === this.country;
        }),
      } as SelectedShippingMethod);

      this.$emit("isEligibleForFreeShipping", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
      });
    },

    triggerVisibility(): void {
      const item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      const visible = item !== undefined;
      this.$emit("shippingMethodVisibilityChanged", { visible: visible });
    },
  },

  watch: {
    totalPriceIncVat(): void {
      if (this.selected) {
        this.$emit("isEligibleForFreeShipping", {
          shipping_method_id: this.shippingMethod.shippingMethodResource.id,
          is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
        });
      }
    },

    country(): void {
      this.triggerVisibility();
      this.$emit("shippingMethodChanged", null);
      this.selected = null;
      this.selectedOption = null;
    },
  },

  created(): void {
    this.triggerVisibility();
  },

  mounted(): void {
    const selectComponent = this.$refs.select as HTMLElement;
    const radioComponent = this.$refs.radio as Vue;

    selectComponent.addEventListener("focus", this.showGlsWidget);
    radioComponent.$el.addEventListener("click", this.showGlsWidget);

    this.options.push({
      value: null,
      text: String(this.$t("shipping_method.gls_point.prompt")),
    });
  },
});
</script>

<style lang="scss"></style>
